"use client";
import React, {
    useEffect,
    useRef,
    useState,
    useMemo,
    useCallback,
} from "react";
import { FaArrowLeft, FaArrowRight } from "react-icons/fa";
import { motion, AnimatePresence } from "framer-motion";
import {
    Dialog,
    DialogContent,
    DialogHeader,
    DialogTitle,
    DialogTrigger,
} from "@/components/ui/dialog";
import { X, ExternalLink } from "lucide-react";
import {
    Tooltip,
    TooltipContent,
    TooltipProvider,
    TooltipTrigger,
} from "@/components/ui/tooltip";

interface Testimonial {
    quote: string;
    name: string;
    designation: string;
    src: string;
}

interface Colors {
    name?: string;
    designation?: string;
    testimony?: string;
    arrowBackground?: string;
    arrowForeground?: string;
    arrowHoverBackground?: string;
}

interface FontSizes {
    name?: string;
    designation?: string;
    quote?: string;
}

interface Logo {
    src: string;
    name: string;
    description: string;
    url?: string | null;
    testimonialIndex?: number;
}

interface CircularTestimonialsProps {
    testimonials: Testimonial[];
    logos?: Logo[];
    autoplay?: boolean;
    readMoreLabel?: string;
    closeLabel?: string;
    visitWebsiteLabel?: string;
    colors?: Colors;
    fontSizes?: FontSizes;
}

function calculateGap(width: number) {
    const minWidth = 1024;
    const maxWidth = 1456;
    const minGap = 60;
    const maxGap = 86;

    if (width <= minWidth) return minGap;
    if (width >= maxWidth)
        return Math.max(minGap, maxGap + 0.06018 * (width - maxWidth));

    return minGap + (maxGap - minGap) * ((width - minWidth) / (maxWidth - minWidth));
}

export const CircularTestimonials = ({
    testimonials,
    logos = [],
    autoplay = true,
    readMoreLabel = "Read more",
    closeLabel = "Close",
    visitWebsiteLabel = "Visit website",
    colors = {},
    fontSizes = {},
}: CircularTestimonialsProps) => {
    // Color & font config
    const colorName = colors.name ?? "#000";
    const colorDesignation = colors.designation ?? "#6b7280";
    const colorTestimony = colors.testimony ?? "#4b5563";
    const colorArrowBg = colors.arrowBackground ?? "#141414";
    const colorArrowFg = colors.arrowForeground ?? "#f1f1f7";
    const colorArrowHoverBg = colors.arrowHoverBackground ?? "#00a6fb";

    const fontSizeName = fontSizes.name ?? "1.5rem";
    const fontSizeDesignation = fontSizes.designation ?? "0.925rem";
    const fontSizeQuote = fontSizes.quote ?? "1.125rem";

    // State
    const [activeIndex, setActiveIndex] = useState(0);
    const [hoverPrev, setHoverPrev] = useState(false);
    const [hoverNext, setHoverNext] = useState(false);
    const [isExpanded, setIsExpanded] = useState(false);
    const [isPaused, setIsPaused] = useState(false);
    const [containerWidth, setContainerWidth] = useState(1200);

    const imageContainerRef = useRef<HTMLDivElement>(null);
    const autoplayIntervalRef = useRef<NodeJS.Timeout | null>(null);

    const testimonialsLength = useMemo(() => testimonials.length, [testimonials]);
    const activeTestimonial = useMemo(
        () => testimonials[activeIndex],
        [activeIndex, testimonials]
    );

    useEffect(() => {
        setIsExpanded(false);
    }, [activeIndex]);

    // Responsive gap calculation
    useEffect(() => {
        // Skip resize logic during prerendering to ensure HTML matches initial client render
        if (typeof window !== 'undefined' && window.__PRERENDER__) return;

        function handleResize() {
            if (imageContainerRef.current) {
                setContainerWidth(imageContainerRef.current.offsetWidth);
            }
        }
        handleResize();
        window.addEventListener("resize", handleResize);
        return () => window.removeEventListener("resize", handleResize);
    }, []);

    // Autoplay
    useEffect(() => {
        // Skip interval during prerender so the server HTML exactly matches initial client state (activeIndex=0)
        if (typeof window !== "undefined" && (window.__PRERENDER__ || navigator.userAgent.includes("AgileCoachPrerender"))) return;

        if (autoplay && !isExpanded && !isPaused) {
            autoplayIntervalRef.current = setInterval(() => {
                setActiveIndex((prev) => (prev + 1) % testimonialsLength);
            }, 15000);
        }
        return () => {
            if (autoplayIntervalRef.current) clearInterval(autoplayIntervalRef.current);
        };
    }, [autoplay, testimonialsLength, isExpanded, isPaused]);

    // Keyboard navigation
    useEffect(() => {
        const handleKey = (e: KeyboardEvent) => {
            if (e.key === "ArrowLeft") handlePrev();
            if (e.key === "ArrowRight") handleNext();
        };
        window.addEventListener("keydown", handleKey);
        return () => window.removeEventListener("keydown", handleKey);
        // eslint-disable-next-line
    }, [activeIndex, testimonialsLength]);

    // Navigation handlers
    const handleNext = useCallback(() => {
        setActiveIndex((prev) => (prev + 1) % testimonialsLength);
        if (autoplayIntervalRef.current) clearInterval(autoplayIntervalRef.current);
    }, [testimonialsLength]);

    const handlePrev = useCallback(() => {
        setActiveIndex((prev) => (prev - 1 + testimonialsLength) % testimonialsLength);
        if (autoplayIntervalRef.current) clearInterval(autoplayIntervalRef.current);
    }, [testimonialsLength]);

    // Compute transforms for each image (always show 3: left, center, right)
    function getImageStyle(index: number): React.CSSProperties {
        const gap = calculateGap(containerWidth);
        const maxStickUp = gap * 0.8;
        const offset = (index - activeIndex + testimonialsLength) % testimonialsLength;
        // const zIndex = testimonialsLength - Math.abs(offset);

        const isActive = index === activeIndex;
        const isLeft = (activeIndex - 1 + testimonialsLength) % testimonialsLength === index;
        const isRight = (activeIndex + 1) % testimonialsLength === index;

        if (isActive) {
            return {
                zIndex: 3,
                opacity: 1,
                pointerEvents: "auto",
                transform: `translateX(0px) translateY(0px) scale(1) rotateY(0deg)`,
                transition: "all 0.8s cubic-bezier(.4,2,.3,1)",
            };
        }

        if (isLeft) {
            return {
                zIndex: 2,
                opacity: 1,
                pointerEvents: "auto",
                transform: `translateX(-${gap}px) translateY(-${maxStickUp}px) scale(0.85) rotateY(15deg)`,
                transition: "all 0.8s cubic-bezier(.4,2,.3,1)",
            };
        }

        if (isRight) {
            return {
                zIndex: 2,
                opacity: 1,
                pointerEvents: "auto",
                transform: `translateX(${gap}px) translateY(-${maxStickUp}px) scale(0.85) rotateY(-15deg)`,
                transition: "all 0.8s cubic-bezier(.4,2,.3,1)",
            };
        }

        // Hide all other images
        return {
            zIndex: 1,
            opacity: 0,
            pointerEvents: "none",
            transition: "all 0.8s cubic-bezier(.4,2,.3,1)",
        };
    }

    // Framer Motion variants for quote
    const quoteVariants = {
        initial: { opacity: 0, y: 20 },
        animate: { opacity: 1, y: 0 },
        exit: { opacity: 0, y: -20 },
    };

    return (
        <div className="w-full max-w-5xl p-8 mx-auto">
            {/* Logo Bar */}
            {logos.length > 0 && (
                <TooltipProvider>
                    <div className="flex flex-wrap justify-center items-center gap-8 md:gap-14 mb-16 animate-fade-in px-4">
                        {logos.map((logo, index) => {
                            const isLinked = logo.testimonialIndex !== undefined;
                            const isActive = isLinked && logo.testimonialIndex === activeIndex;

                            return (
                                <Tooltip key={index} delayDuration={300}>
                                    <TooltipTrigger asChild>
                                        <button
                                            onClick={() => {
                                                if (isLinked) {
                                                    setActiveIndex(logo.testimonialIndex!);
                                                    if (autoplayIntervalRef.current) clearInterval(autoplayIntervalRef.current);
                                                }
                                            }}
                                            className={`transition-all duration-700 ease-in-out transform hover:scale-110 focus:outline-none ${isActive
                                                ? "grayscale-0 opacity-100 scale-110"
                                                : "grayscale opacity-30 hover:opacity-100"
                                                } ${isLinked ? "cursor-pointer" : "cursor-default"}`}
                                        >
                                            <img
                                                src={logo.src}
                                                alt={logo.name}
                                                className="h-8 md:h-10 w-auto object-contain"
                                            />
                                        </button>
                                    </TooltipTrigger>
                                    <TooltipContent className="max-w-[200px] p-3 text-center bg-card border-primary/20 shadow-xl">
                                        <p className="font-bold text-sm mb-1">{logo.name}</p>
                                        <p className="text-xs text-muted-foreground mb-2 line-clamp-3">
                                            {logo.description}
                                        </p>
                                        {logo.url && (
                                            <a
                                                href={logo.url}
                                                target="_blank"
                                                rel="noopener noreferrer"
                                                className="text-[10px] text-primary flex items-center justify-center gap-1 hover:underline"
                                                onClick={(e) => e.stopPropagation()}
                                            >
                                                {visitWebsiteLabel} <ExternalLink className="h-2 w-2" />
                                            </a>
                                        )}
                                    </TooltipContent>
                                </Tooltip>
                            );
                        })}
                    </div>
                </TooltipProvider>
            )}

            <div className="grid gap-12 md:gap-20 md:grid-cols-[0.7fr_1.3fr] items-start">
                {/* Images */}
                <div className="relative w-full h-64 md:h-[320px] [perspective:1000px]" ref={imageContainerRef}>
                    {testimonials.map((testimonial, index) => (
                        <img
                            key={testimonial.src}
                            src={testimonial.src}
                            alt={testimonial.name}
                            className="absolute w-full h-full object-cover rounded-xl shadow-xl"
                            data-index={index}
                            suppressHydrationWarning
                            style={getImageStyle(index)}
                        />
                    ))}
                </div>

                {/* Content */}
                <div className="relative flex flex-col justify-between min-h-[280px] md:min-h-[320px]">
                    <div className="flex-1 flex flex-col justify-center">
                        <AnimatePresence mode="wait">
                            <motion.div
                                key={activeIndex}
                                variants={quoteVariants}
                                initial="initial"
                                animate="animate"
                                exit="exit"
                                transition={{ duration: 0.3, ease: "easeInOut" }}
                            >
                                <h3
                                    className="font-bold mb-1"
                                    style={{ color: colorName, fontSize: fontSizeName }}
                                >
                                    {activeTestimonial.name}
                                </h3>
                                <p
                                    className="mb-8"
                                    style={{ color: colorDesignation, fontSize: fontSizeDesignation }}
                                >
                                    {activeTestimonial.designation}
                                </p>
                                <motion.div
                                    className="leading-relaxed"
                                    style={{ color: colorTestimony, fontSize: fontSizeQuote }}
                                >
                                    <span className="line-clamp-4">
                                        {activeTestimonial.quote.split(" ").map((word, i) => (
                                            <motion.span
                                                key={i}
                                                initial={{
                                                    filter: "blur(10px)",
                                                    opacity: 0,
                                                    y: 5,
                                                }}
                                                animate={{
                                                    filter: "blur(0px)",
                                                    opacity: 1,
                                                    y: 0,
                                                }}
                                                transition={{
                                                    duration: 0.22,
                                                    ease: "easeInOut",
                                                    delay: 0.025 * i,
                                                }}
                                                style={{ display: "inline-block" }}
                                            >
                                                {word}&nbsp;
                                            </motion.span>
                                        ))}
                                    </span>
                                    {activeTestimonial.quote.length > 200 && (
                                        <Dialog onOpenChange={(open) => setIsPaused(open)}>
                                            <DialogTrigger asChild>
                                                <button
                                                    className="text-sm font-semibold ml-2 hover:underline focus:outline-none"
                                                    style={{ color: colorArrowHoverBg }}
                                                >
                                                    ...{readMoreLabel}
                                                </button>
                                            </DialogTrigger>
                                            <DialogContent className="max-w-5xl bg-card border-primary/20">
                                                <DialogHeader>
                                                    <div className="flex items-center gap-4 mb-4">
                                                        <img
                                                            src={activeTestimonial.src}
                                                            alt={activeTestimonial.name}
                                                            className="w-16 h-16 rounded-full object-cover border-2 border-primary/20"
                                                        />
                                                        <div>
                                                            <DialogTitle className="text-xl font-bold">{activeTestimonial.name}</DialogTitle>
                                                            <p className="text-sm text-muted-foreground">{activeTestimonial.designation}</p>
                                                        </div>
                                                    </div>
                                                </DialogHeader>
                                                <div className="text-lg leading-relaxed text-muted-foreground whitespace-pre-wrap italic border-l-4 border-primary/20 pl-6 py-2">
                                                    "{activeTestimonial.quote}"
                                                </div>
                                                <div className="flex justify-end mt-6">
                                                    <DialogTrigger asChild>
                                                        <button className="px-4 py-2 bg-primary text-primary-foreground rounded-lg hover:bg-primary/90 transition-colors">
                                                            {closeLabel}
                                                        </button>
                                                    </DialogTrigger>
                                                </div>
                                            </DialogContent>
                                        </Dialog>
                                    )}
                                </motion.div>
                            </motion.div>
                        </AnimatePresence>
                    </div>

                    <div className="flex gap-6 mt-12 md:mt-0">
                        <button
                            className="w-11 h-11 rounded-full flex items-center justify-center cursor-pointer transition-colors duration-300 border-none group"
                            onClick={handlePrev}
                            style={{
                                backgroundColor: hoverPrev ? colorArrowHoverBg : colorArrowBg,
                            }}
                            onMouseEnter={() => setHoverPrev(true)}
                            onMouseLeave={() => setHoverPrev(false)}
                            aria-label="Previous testimonial"
                        >
                            <FaArrowLeft size={28} color={colorArrowFg} className="transition-transform group-hover:-translate-x-1" />
                        </button>
                        <button
                            className="w-11 h-11 rounded-full flex items-center justify-center cursor-pointer transition-colors duration-300 border-none group"
                            onClick={handleNext}
                            style={{
                                backgroundColor: hoverNext ? colorArrowHoverBg : colorArrowBg,
                            }}
                            onMouseEnter={() => setHoverNext(true)}
                            onMouseLeave={() => setHoverNext(false)}
                            aria-label="Next testimonial"
                        >
                            <FaArrowRight size={28} color={colorArrowFg} className="transition-transform group-hover:translate-x-1" />
                        </button>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default CircularTestimonials;
